/**
 * The hooks views use: the services from context, the live session and
 * theme, and the data hooks — each a one-line binding of a `…Query` /
 * `…Mutation` factory (`src/api/`) to this app's backend.
 *
 * Only views (`src/views/`) call these. Presentational components never do:
 * `stories.test.ts` fails a component that imports from `app/`, `api/`
 * (values), `auth/` or TanStack Query.
 */
import type { ResourceKindWire } from '@acp/api-contract/changes.js';
import { createUlidFactory } from '@acp/domain/ulid.js';
import { type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react';
import { besideNodeFeed, type ChangeFeedDeps, createChangeFeedSession, LIST_KEYS } from '../api/changes.js';
import type { HeardChange } from '../lib/live/changeLog.js';
import { absorb, initialLive, type LiveActions, liveActions, type LiveNotice, type LiveState } from '../lib/live/liveResource.js';
import { cloudProductionsQuery, launchCloudProductionMutation, stopCloudProductionMutation } from '../api/cloudProductions.js';
import {
  acknowledgeAlertMutation,
  alertHistoryQuery,
  alertPickersQuery,
  alertSettingsQuery,
  alertsQuery,
  createAlertChannelMutation,
  deleteAlertChannelMutation,
  saveAlertSettingsMutation,
  testAlertMutation,
  updateAlertChannelMutation,
} from '../api/alerts.js';
import { createFirstRundownMutation, createSiteWithCodeMutation, onboardingQuery, updateOnboardingMutation } from '../api/onboarding.js';
import { activeProductionsQuery, onAirPartsQuery, productionsRunning } from '../api/onAir.js';
import { createWriter } from '../lib/editor/writer.js';
import type { AlertType } from '@acp/tenant-alerts/catalog.js';
import { meQuery } from '../api/identity.js';
import {
  acceptInviteMutation,
  auditQuery,
  cancelEnrolmentMutation,
  createGroupMutation,
  createSiteMutation,
  deleteDeviceSecretMutation,
  deleteGroupMutation,
  deleteNodeMutation,
  deleteSiteMutation,
  deviceSecretsQuery,
  emergencyAccountMutation,
  emergencyQuery,
  enrolmentCodeMutation,
  groupMemberMutation,
  groupQuery,
  groupsQuery,
  inspectInviteQuery,
  inviteUserMutation,
  nodesQuery,
  offlinePinMutation,
  offlinePinQuery,
  pendingEnrolmentsQuery,
  removeNodeMutation,
  renameSiteMutation,
  replaceNodeMutation,
  saveGroupMutation,
  setDeviceSecretMutation,
  setUserGroupsMutation,
  sitesQuery,
  swapRolesMutation,
  updateNodeMutation,
  usageQuery,
  userActionMutation,
  usersQuery,
} from '../api/admin.js';
import { localAuthQuery } from '../api/localAuth.js';
import { createRundownEditorSession, type EditorSession, type EditorSessionDeps, type EditorSnapshot } from '../api/rundownEditor.js';
import { createPlayoutSession, type PlayoutNotice, type PlayoutSession, type PlayoutSessionDeps, type PlayoutSnapshot } from '../api/playout.js';
import { asRunQuery, createProductionMutation, endProductionMutation, playlistsQuery, productionQuery, productionsQuery, switchoverMutation } from '../api/productions.js';
import { createRundownMutation, rundownsQuery } from '../api/rundowns.js';
import {
  createShowStyleMutation,
  deleteShowStyleMutation,
  saveShowStyleMutation,
  showStyleQuery,
  showStylesQuery,
  validateShowStyleQuery,
} from '../api/showStyles.js';
import { createStudioMutation, deleteStudioMutation, saveStudioMutation, studioQuery, studiosQuery } from '../api/studios.js';
import { forcePromoteMutation, pairSwitchoverMutation, redundancyOverviewQuery, standbyPlayoutQuery, switchoverAllMutation, updateRedundancyMutation } from '../api/redundancy.js';
import { announcementsQuery } from '../api/announcements.js';
import {
  changePinMutation,
  createAnnouncementMutation,
  createTenantMutation,
  deleteAnnouncementMutation,
  reactivateTenantMutation,
  saveAnnouncementMutation,
  suspendTenantMutation,
  vendorActivityQuery,
  vendorAnnouncementQuery,
  vendorAnnouncementsQuery,
  vendorAuditQuery,
  vendorOpsQuery,
  vendorReleasesQuery,
  vendorTenantQuery,
  vendorTenantsQuery,
} from '../api/vendor.js';
import { type Grants, grantsOf } from '../lib/permissions.js';
import type { Session } from '../auth/session.js';
import type { ConnectivityState } from '../connectivity/state.js';
import type { Theme } from '../lib/theme.js';
import { routeTarget, type RouteTarget } from '../connectivity/routing.js';
import { ROUTES, type RouteId } from '@acp/api-contract/routes.js';
import { browserRandomBytes, browserSocket, browserTimers } from './browserPorts.js';
import type { Services } from './services.js';

export const ServicesContext = createContext<Services | null>(null);

export function useServices(): Services {
  const services = useContext(ServicesContext);
  if (services === null) throw new Error('useServices() outside <ServicesContext.Provider> — views render inside startApp()');
  return services;
}

export function useSession(): Session | null {
  const { sessions } = useServices();
  return useSyncExternalStore(sessions.subscribe, sessions.getSession, sessions.getSession);
}

export function useTheme(): Theme {
  const { theme } = useServices();
  return useSyncExternalStore(theme.subscribe, theme.get, theme.get);
}

/** The live connectivity state (PLAN §10.2): every watched endpoint, the primary, the socket. */
export function useConnectivity(): ConnectivityState {
  const { connectivity } = useServices();
  return useSyncExternalStore(connectivity.subscribe, connectivity.getState, connectivity.getState);
}

/** The primary backend's endpoint id: changes only on a failover or the operator's switch (not on every probe). */
export function usePrimaryId(): string {
  const { connectivity } = useServices();
  const primaryId = () => connectivity.getState().primaryId;
  return useSyncExternalStore(connectivity.subscribe, primaryId, primaryId);
}

/**
 * Whether route `id` can be sent from this page now, and where (`connectivity/routing.ts`): the primary, the cloud for
 * a cloud-only route from a node's page, or — unavailable — why. Follows a failover, the cloud's reachability and the
 * session, so a screen flips between itself and "needs a cloud connection" as they change.
 */
export function useRouteAvailability(id: RouteId): RouteTarget {
  const state = useConnectivity();
  const session = useSession();
  return routeTarget(ROUTES[id], state, session?.provider ?? null);
}

/**
 * On a page served by a node whose site has no studio (the node replicates only its site's studios and what runs in
 * them: docs/sitelink.md), the site to assign one to — the serving node's, from its `/health` — else null (a cloud
 * page, studios not read yet, or some there).
 */
export function useNodeSiteWithoutStudios(): { readonly siteId: string | null } | null {
  const services = useServices();
  const studios = useStudios();
  const state = useConnectivity();
  // A node with no cloud (a lab, the e2e node) keeps what is made on it: nothing is assigned anywhere else.
  const linked = (services.config.status?.cloud ?? null) !== null;
  if (services.config.backend.kind !== 'node' || !linked || studios.data === undefined || studios.data.length > 0) return null;
  const serving = state.endpoints.find((s) => s.endpoint.servesPage);
  return { siteId: serving?.node?.siteId ?? null };
}

/** The node's sign-in status (`GET /local/auth`, D20), polled; idle when the page is not served by a node. */
export function useLocalAuth() {
  return useQuery(localAuthQuery(useServices().offline?.status ?? null));
}

export function useRundowns() {
  return useQuery(rundownsQuery(useServices().backend.api));
}

export function useCreateRundown() {
  const queryClient = useQueryClient();
  return useMutation(createRundownMutation(useServices().backend.api, queryClient));
}

export function useMe() {
  return useQuery(meQuery(useServices().backend.api));
}

/**
 * The live editor session for one rundown (`api/rundownEditor.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * rundown.
 */
export function useRundownEditorSession(rundownId: string, notify: EditorSessionDeps['notify']): EditorSession {
  const services = useServices();
  const { backend, sessions, connectivity } = services;
  const tenantId = useSession()?.tenantId ?? '';
  const primaryId = usePrimaryId();
  const [session] = useState(() =>
    createRundownEditorSession({
      api: backend.api,
      // Read at every subscribe: the getter names the CURRENT primary.
      wsUrl: () => backend.wsUrl,
      reportSocket: connectivity.reportSocket,
      tenantId,
      rundownId,
      token: () => (backend.kind === 'node' ? sessions.nodeToken(false) : sessions.idToken(false)),
      openSocket: browserSocket,
      timers: browserTimers,
      nowMs: services.nowMs,
      parsing: services.responses,
      randomBytes: browserRandomBytes,
      notify,
    }),
  );
  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);
  // A failover or the operator's switch: reload from the new primary and resubscribe there.
  useEffect(() => {
    session.setPrimary(primaryId);
  }, [session, primaryId]);
  return session;
}

/** The session's snapshot, re-rendering on every change. */
export function useEditorSnapshot(session: EditorSession): EditorSnapshot {
  return useSyncExternalStore(session.subscribe, session.snapshot, session.snapshot);
}

/** The productions running a rundown (or its playlist) now: the tenant's active ones, filtered (`api/onAir.ts`). */
export function useRunningProductions(rundownId: string, playlistId: string | null) {
  return productionsRunning(useQuery(activeProductionsQuery(useServices().backend.api)).data ?? [], rundownId, playlistId);
}

/** The parts of a rundown on air / next in the productions running it, polled (`api/onAir.ts`). */
export function useOnAirParts(rundownId: string, playlistId: string | null) {
  const api = useServices().backend.api;
  const running = useRunningProductions(rundownId, playlistId).map((p) => p.productionId);
  return useQuery(onAirPartsQuery(api, rundownId, running));
}

/** The signed-in user's grants (D14) for hiding and disabling controls; null (unknown) on a node, which serves no `/me`. */
export function useGrants(): Grants {
  return grantsOf(useMe().data);
}

/** The browser's clock, re-read every `intervalMs` (the playout view's countdowns tick on it). */
export function useNow(intervalMs: number): number {
  const { nowMs } = useServices();
  const [now, setNow] = useState(nowMs);
  useEffect(() => browserTimers.every(intervalMs, () => setNow(nowMs())), [intervalMs, nowMs]);
  return now;
}

/** A ULID factory on the browser's clock and randomness (ids the client mints: productions, commands). */
export function useIdFactory(): () => string {
  const { nowMs } = useServices();
  const [newId] = useState(() => createUlidFactory(nowMs, browserRandomBytes));
  return newId;
}

// ─── studios ───

export function useStudios() {
  return useQuery(studiosQuery(useServices().backend.api));
}

export function useStudio(studioId: string) {
  return useQuery(studioQuery(useServices().backend.api, studioId));
}

/** One studio, or nothing fetched for `null` (a page with no studio chosen yet). */
export function useMaybeStudio(studioId: string | null) {
  return useQuery({ ...studioQuery(useServices().backend.api, studioId ?? ''), enabled: studioId !== null });
}

export function useCreateStudio() {
  const queryClient = useQueryClient();
  return useMutation(createStudioMutation(useServices().backend.api, queryClient));
}

export function useSaveStudio() {
  const queryClient = useQueryClient();
  return useMutation(saveStudioMutation(useServices().backend.api, queryClient));
}

// ─── show styles (D32) ───

export function useShowStyles() {
  return useQuery(showStylesQuery(useServices().backend.api));
}

/** One show style; `null` fetches nothing (a studio with no show style chosen). */
export function useShowStyle(showStyleId: string | null) {
  return useQuery({ ...showStyleQuery(useServices().backend.api, showStyleId ?? ''), enabled: showStyleId !== null });
}

/** The server's judgement of a pair; `null` for either fetches nothing. */
export function useValidateShowStyle(showStyleId: string | null, studioId: string | null) {
  return useQuery({ ...validateShowStyleQuery(useServices().backend.api, showStyleId ?? '', studioId ?? ''), enabled: showStyleId !== null && studioId !== null });
}

export function useCreateShowStyle() {
  const queryClient = useQueryClient();
  return useMutation(createShowStyleMutation(useServices().backend.api, queryClient));
}

export function useSaveShowStyle() {
  const queryClient = useQueryClient();
  return useMutation(saveShowStyleMutation(useServices().backend.api, queryClient));
}

export function useDeleteShowStyle() {
  const queryClient = useQueryClient();
  return useMutation(deleteShowStyleMutation(useServices().backend.api, queryClient));
}

// ─── productions ───

export function useProductions() {
  return useQuery(productionsQuery(useServices().backend.api));
}

export function useProduction(productionId: string) {
  return useQuery(productionQuery(useServices().backend.api, productionId));
}

export function usePlaylists() {
  return useQuery(playlistsQuery(useServices().backend.api));
}

export function useCreateProduction() {
  const queryClient = useQueryClient();
  const newId = useIdFactory();
  return useMutation(createProductionMutation(useServices().backend.api, queryClient, newId));
}

export function useEndProduction() {
  const queryClient = useQueryClient();
  return useMutation(endProductionMutation(useServices().backend.api, queryClient));
}

/** A manual switchover of a production to the other node of its pair (PLAN §8.4). */
export function useSwitchover() {
  return useMutation(switchoverMutation(useServices().backend.api));
}

// ─── cloud productions (PLAN §9) ───

/** The cloud productions and the network they run in; re-read every few seconds while one is on the move. */
export function useCloudProductions() {
  return useQuery(cloudProductionsQuery(useServices().backend.api));
}

export function useLaunchCloudProduction() {
  const queryClient = useQueryClient();
  const newId = useIdFactory();
  return useMutation(launchCloudProductionMutation(useServices().backend.api, queryClient, newId));
}

export function useStopCloudProduction() {
  const queryClient = useQueryClient();
  return useMutation(stopCloudProductionMutation(useServices().backend.api, queryClient));
}

export function useAsRun(productionId: string) {
  return useInfiniteQuery(asRunQuery(useServices().backend.api, productionId));
}

// ─── tenant alerts (phase 7b) ───

/** The alerts the caller may see (`available: false` on a node, which keeps none). Live through the change channel. */
export function useAlerts(status: 'active' | 'all') {
  return useQuery(alertsQuery(useServices().backend.api, status));
}

export function useAcknowledgeAlert() {
  const { backend } = useServices();
  return useMutation(acknowledgeAlertMutation(backend.api, useQueryClient()));
}

export function useAlertHistory(type: AlertType | null) {
  return useInfiniteQuery(alertHistoryQuery(useServices().backend.api, type));
}

export function useAlertSettings() {
  return useQuery(alertSettingsQuery(useServices().backend.api));
}

/** The groups and sites the add-channel dialog offers. */
export function useAlertPickers() {
  const pickers = alertPickersQuery(useServices().backend.api);
  return { groups: useQuery(pickers.groups), sites: useQuery(pickers.sites) };
}

export function useSaveAlertSettings() {
  const queryClient = useQueryClient();
  return useMutation(saveAlertSettingsMutation(useServices().backend.api, queryClient));
}

export function useCreateAlertChannel() {
  const queryClient = useQueryClient();
  return useMutation(createAlertChannelMutation(useServices().backend.api, queryClient));
}

export function useUpdateAlertChannel() {
  const queryClient = useQueryClient();
  return useMutation(updateAlertChannelMutation(useServices().backend.api, queryClient));
}

export function useDeleteAlertChannel() {
  const queryClient = useQueryClient();
  return useMutation(deleteAlertChannelMutation(useServices().backend.api, queryClient));
}

export function useTestAlert() {
  return useMutation(testAlertMutation(useServices().backend.api));
}

// ─── onboarding (phase 7b) ───

/** The wizard's saved progress; `enabled: false` asks nothing (a caller who is not a tenant admin). */
export function useOnboarding(enabled: boolean) {
  return useQuery({ ...onboardingQuery(useServices().backend.api), enabled });
}

/**
 * What the first-run check reads (`lib/onboarding.ts`' `shouldAutoOpen`): the tenant's show styles, studios and
 * rundowns — only once the saved progress says the wizard was never opened (`enabled`).
 */
export function useFirstRunFacts(enabled: boolean) {
  const api = useServices().backend.api;
  const showStyles = useQuery({ ...showStylesQuery(api), enabled });
  const studios = useQuery({ ...studiosQuery(api), enabled });
  const rundowns = useQuery({ ...rundownsQuery(api), enabled });
  if (showStyles.data === undefined || studios.data === undefined || rundowns.data === undefined) return null;
  return { showStyleIds: showStyles.data.map((s) => s.showStyleId), studioIds: studios.data.map((s) => s.studioId), rundownIds: rundowns.data.map((r) => r.rundownId) };
}

export function useUpdateOnboarding() {
  const queryClient = useQueryClient();
  return useMutation(updateOnboardingMutation(useServices().backend.api, queryClient));
}

export function useCreateSiteWithCode() {
  const queryClient = useQueryClient();
  return useMutation(createSiteWithCodeMutation(useServices().backend.api, queryClient));
}

/** Step 4's rundown, seeded through this tab's own writer (a fresh HLC node id, D6). */
export function useCreateFirstRundown() {
  const queryClient = useQueryClient();
  const { nowMs } = useServices();
  const [writer] = useState(() => createWriter(nowMs, browserRandomBytes));
  const source = () => ({ hlc: writer.clock.now, newId: writer.newId, newOpId: writer.newOpId, origin: writer.nodeId, ordTag: writer.ordTag, textClientId: writer.textClientId() });
  return useMutation(createFirstRundownMutation(useServices().backend.api, queryClient, source));
}

/**
 * The live playout session for one production (`api/playout.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * production.
 */
/** The ports a playout session takes, from the app's services (the browser's socket and timers). */
export function playoutSessionDeps(services: Services, tenantId: string, productionId: string, newId: () => string, notify: (notice: PlayoutNotice) => void): PlayoutSessionDeps {
  const { backend, sessions, connectivity } = services;
  return {
    api: backend.api,
    // Read at every subscribe: the getter names the CURRENT primary.
    wsUrl: () => backend.wsUrl,
    reportSocket: connectivity.reportSocket,
    tenantId,
    productionId,
    token: () => (backend.kind === 'node' ? sessions.nodeToken(false) : sessions.idToken(false)),
    openSocket: browserSocket,
    timers: browserTimers,
    nowMs: services.nowMs,
    parsing: services.responses,
    newCommandId: () => `web-${newId()}`,
    notify,
  };
}

/**
 * The live playout session for one production (`api/playout.ts`), created
 * once per mount and started/stopped with it. Remount (a `key`) for another
 * production.
 */
export function usePlayoutSession(productionId: string, notify: (notice: PlayoutNotice) => void): PlayoutSession {
  const services = useServices();
  const tenantId = useSession()?.tenantId ?? '';
  const primaryId = usePrimaryId();
  const newId = useIdFactory();
  const [session] = useState(() => createPlayoutSession(playoutSessionDeps(services, tenantId, productionId, newId, notify)));
  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);
  useEffect(() => {
    session.setPrimary(primaryId);
  }, [session, primaryId]);
  return session;
}

export function usePlayoutSnapshot(session: PlayoutSession): PlayoutSnapshot {
  return useSyncExternalStore(session.subscribe, session.snapshot, session.snapshot);
}

// ─── the vendor console (phase 7b) ───

export function useVendorTenants() {
  return useQuery(vendorTenantsQuery(useServices().backend.api));
}

export function useVendorTenant(tenantId: string) {
  return useQuery(vendorTenantQuery(useServices().backend.api, tenantId));
}

export function useCreateTenant() {
  const queryClient = useQueryClient();
  return useMutation(createTenantMutation(useServices().backend.api, queryClient));
}

export function useSuspendTenant() {
  const queryClient = useQueryClient();
  return useMutation(suspendTenantMutation(useServices().backend.api, queryClient));
}

export function useReactivateTenant() {
  const queryClient = useQueryClient();
  return useMutation(reactivateTenantMutation(useServices().backend.api, queryClient));
}

/** The ops dashboard; re-read every 30 s (`OPS_POLL_MS`). */
export function useVendorOps() {
  return useQuery(vendorOpsQuery(useServices().backend.api));
}

export function useVendorReleases() {
  return useQuery(vendorReleasesQuery(useServices().backend.api));
}

export function useChangePin() {
  const queryClient = useQueryClient();
  return useMutation(changePinMutation(useServices().backend.api, queryClient));
}

export function useVendorActivity() {
  return useInfiniteQuery(vendorActivityQuery(useServices().backend.api));
}

export function useVendorAudit() {
  return useInfiniteQuery(vendorAuditQuery(useServices().backend.api));
}

export function useVendorAnnouncements() {
  return useQuery(vendorAnnouncementsQuery(useServices().backend.api));
}

export function useVendorAnnouncement(announcementId: string) {
  return useQuery(vendorAnnouncementQuery(useServices().backend.api, announcementId));
}

export function useCreateAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation(createAnnouncementMutation(useServices().backend.api, queryClient));
}

export function useSaveAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation(saveAnnouncementMutation(useServices().backend.api, queryClient));
}

export function useDeleteAnnouncement() {
  const queryClient = useQueryClient();
  return useMutation(deleteAnnouncementMutation(useServices().backend.api, queryClient));
}

/** The vendor's announcements in force for my tenant: cloud only (a node has no such route). */
export function useAnnouncements() {
  const { backend } = useServices();
  return useQuery({ ...announcementsQuery(backend.api), enabled: backend.kind === 'cloud' });
}

/** The announcements this browser dismissed (`lib/dismissals.ts`), re-rendering on a dismissal. */
export function useDismissals(): readonly string[] {
  const { dismissals } = useServices();
  return useSyncExternalStore(dismissals.subscribe, dismissals.get, dismissals.get);
}

// ─── live updates (D35) ───

/** The ports the page's change feed takes, from the app's services (the browser's socket and timers). */
export function changeFeedDeps(services: Services, tenantId: string, queryClient: Pick<QueryClient, 'invalidateQueries'>): ChangeFeedDeps {
  const { backend, sessions, connectivity } = services;
  return {
    // Read at every connect: the getter names the CURRENT primary.
    wsUrl: () => backend.wsUrl,
    tenantId,
    token: () => (backend.kind === 'node' ? sessions.nodeToken(false) : sessions.idToken(false)),
    openSocket: browserSocket,
    timers: browserTimers,
    nowMs: services.nowMs,
    parsing: services.responses,
    reportSocket: connectivity.reportSocket,
    invalidate: (queryKey) => queryClient.invalidateQueries({ queryKey }),
    log: services.liveChanges,
    accept: () => true,
    resumeKeys: LIST_KEYS,
  };
}

/**
 * The page's tenant change channel: one socket while signed in (`AppLayout`
 * mounts it, on a cloud page and a node's alike), each `resource.changed`
 * invalidating the matching queries (`api/changes.ts`: every list and detail
 * refetches on its own) and landing in the change log for open editors.
 */
export function useLiveChanges(): void {
  const services = useServices();
  const queryClient = useQueryClient();
  const tenantId = useSession()?.tenantId ?? '';
  const primaryId = usePrimaryId();
  const [session] = useState(() => createChangeFeedSession(changeFeedDeps(services, tenantId, queryClient)));
  useEffect(() => {
    session.start();
    return () => session.stop();
  }, [session]);
  useEffect(() => {
    session.setPrimary(primaryId);
  }, [session, primaryId]);
  // A node primary: the cloud-only screens' calls go to the cloud (host-aware routing), so their changes come from
  // the cloud's channel — a second socket, one per backend, for those kinds only (`cloudOnlyKind`).
  const cloudRoute = useRouteAvailability('listGroups');
  const cloudWsUrl = cloudRoute.ok && cloudRoute.endpoint.kind === 'cloud' && cloudRoute.endpoint.id !== primaryId ? cloudRoute.endpoint.wsUrl : null;
  useEffect(() => {
    if (cloudWsUrl === null) return;
    const cloud = createChangeFeedSession(besideNodeFeed(changeFeedDeps(services, tenantId, queryClient), cloudWsUrl, services.sessions.idToken));
    cloud.start();
    return () => cloud.stop();
  }, [cloudWsUrl, services, tenantId, queryClient]);
}

export type LiveResourceOptions<T> = {
  readonly kind: ResourceKindWire;
  readonly id: string;
  /** The version the page opened with. */
  readonly loaded: T;
  /** The query's data now: a refetch after a change (or our own save's result) lands here. */
  readonly latest: T | undefined;
  /** Whether the draft differs from `base` (the page's own judgement), asked about the hook's current base. */
  readonly isDirty: (base: T) => boolean;
  readonly versionOf: (value: T) => number;
  /** Who wrote a version, when the change channel has not said (its `updatedBy`). */
  readonly authorOf: (value: T) => string;
  /** When a version was written (ms), when the change channel has not said. */
  readonly writtenAtOf: (value: T) => number;
  /** Replace the draft with this version (refreshed in place, theirs loaded, or ours saved). */
  readonly onReset: (value: T) => void;
};

/**
 * An editor's live state and actions: `beginSave` before a save (what the query shows meanwhile is ours in
 * flight), `saved` / `conflict` (its 409: the server's version, the same two choices as a change heard) /
 * `saveFailed` after; `keepMine` ("Overwrite with mine": re-base on theirs, returns the version to save against)
 * and `discardMine` ("Discard mine and load theirs").
 */
export type LiveResource<T> = LiveActions<T> & {
  /** The version the draft is made from: what a save names. */
  readonly base: T;
  readonly notice: LiveNotice<T> | null;
  /** How many times the page was refreshed in place: a keyed overlay flashes on each (`animate-live-flash`). */
  readonly refreshes: number;
};

/**
 * The dirty-aware editor (D35, `lib/live/liveResource.ts`) bound to the
 * page: the query's data and the change log in, the base / notice /
 * highlight out, and the draft reset through `onReset`. Any editor of a
 * versioned resource uses it the same way — the studio and show-style pages
 * now, the admin screens next.
 */
export function useLiveResource<T>(options: LiveResourceOptions<T>): LiveResource<T> {
  const { kind, id, latest, versionOf, onReset } = options;
  const { liveChanges } = useServices();
  const lastHeard = () => liveChanges.last(kind, id);
  const heard = useSyncExternalStore(liveChanges.subscribe, lastHeard, lastHeard);
  const [state, setState] = useState<LiveState<T>>(() => initialLive(options.loaded));
  const [seenLatest, setSeenLatest] = useState<T | undefined>(latest);
  const [seenHeard, setSeenHeard] = useState<HeardChange | null>(heard);
  const [saving, setSaving] = useState(false);

  const reader = { versionOf, authorOf: options.authorOf, writtenAtOf: options.writtenAtOf };

  // Adjusting state to new inputs during render (React's documented pattern; no effect, no extra frame).
  if (heard !== seenHeard || latest !== seenLatest) {
    const absorbed = absorb(state, { heard, seenHeard, latest, seenLatest, saving, isDirty: options.isDirty }, reader);
    setSeenHeard(heard);
    setSeenLatest(latest);
    if (absorbed.state !== state) setState(absorbed.state);
    if (absorbed.reset !== null) onReset(absorbed.reset);
  }

  return { base: state.base, notice: state.notice, refreshes: state.refreshes, ...liveActions({ state, setState, setSaving, onReset, heard, reader }) };
}

// ─── tenant admin (Phase 7b; api/admin.ts) ───

/** Bind a mutation factory taking the API and the query client. */
function useAdminMutation<T>(factory: (api: Services['backend']['api'], queryClient: QueryClient) => T): T {
  const queryClient = useQueryClient();
  return factory(useServices().backend.api, queryClient);
}

export function useAdminUsers() {
  return useQuery(usersQuery(useServices().backend.api));
}

export function useAdminGroups() {
  return useQuery(groupsQuery(useServices().backend.api));
}

export function useAdminGroup(groupId: string) {
  return useQuery(groupQuery(useServices().backend.api, groupId));
}

export function useAdminSites() {
  return useQuery(sitesQuery(useServices().backend.api));
}

export function useSiteNodes(siteId: string) {
  return useQuery(nodesQuery(useServices().backend.api, siteId));
}

export function useEmergencyAccount(siteId: string) {
  return useQuery(emergencyQuery(useServices().backend.api, siteId));
}

/** A studio's device secrets (never their values); `null` fetches nothing. */
export function useDeviceSecrets(studioId: string | null) {
  return useQuery({ ...deviceSecretsQuery(useServices().backend.api, studioId ?? ''), enabled: studioId !== null });
}

export function useAudit() {
  return useInfiniteQuery(auditQuery(useServices().backend.api));
}

export function useUsage(month: string) {
  return useQuery(usageQuery(useServices().backend.api, month));
}

export function useOfflinePin() {
  return useQuery(offlinePinQuery(useServices().backend.api));
}

/** The invite a set-password link names (public: no token is sent). */
export function useInspectInvite(token: string | null) {
  return useQuery({ ...inspectInviteQuery(useServices().publicApi, token ?? ''), enabled: token !== null });
}

export function useAcceptInvite() {
  return useMutation(acceptInviteMutation(useServices().publicApi));
}

export function useInviteUser() {
  return useMutation(useAdminMutation(inviteUserMutation));
}

export function useUserAction() {
  return useMutation(useAdminMutation(userActionMutation));
}

export function useSetUserGroups() {
  return useMutation(useAdminMutation(setUserGroupsMutation));
}

export function useGroupMember() {
  return useMutation(useAdminMutation(groupMemberMutation));
}

export function useCreateGroup() {
  return useMutation(useAdminMutation(createGroupMutation));
}

export function useSaveGroup() {
  return useMutation(useAdminMutation(saveGroupMutation));
}

export function useDeleteGroup() {
  return useMutation(useAdminMutation(deleteGroupMutation));
}

export function useCreateSite() {
  return useMutation(useAdminMutation(createSiteMutation));
}

export function useRenameSite() {
  return useMutation(useAdminMutation(renameSiteMutation));
}

export function useDeleteSite() {
  return useMutation(useAdminMutation(deleteSiteMutation));
}

export function useEnrolmentCode() {
  return useMutation(useAdminMutation(enrolmentCodeMutation));
}

export function usePendingEnrolments(siteId: string, enabled: boolean) {
  return useQuery({ ...pendingEnrolmentsQuery(useServices().backend.api, siteId), enabled });
}

export function useCancelEnrolment() {
  return useMutation(useAdminMutation(cancelEnrolmentMutation));
}

export function useRemoveNode() {
  return useMutation(useAdminMutation(removeNodeMutation));
}

export function useDeleteNode() {
  return useMutation(useAdminMutation(deleteNodeMutation));
}

export function useSwapRoles() {
  return useMutation(useAdminMutation(swapRolesMutation));
}

export function useUpdateNode() {
  return useMutation(useAdminMutation(updateNodeMutation));
}

export function useReplaceNode() {
  return useMutation(useAdminMutation(replaceNodeMutation));
}

export function useEmergencyAccountWrite() {
  return useMutation(useAdminMutation(emergencyAccountMutation));
}

export function useSetDeviceSecret() {
  return useMutation(useAdminMutation(setDeviceSecretMutation));
}

export function useDeleteDeviceSecret() {
  return useMutation(useAdminMutation(deleteDeviceSecretMutation));
}

export function useOfflinePinWrite() {
  return useMutation(useAdminMutation(offlinePinMutation));
}

// ─── main/backup (wave 7b-tail C) ───

/** Every site's pair overview the caller may see (or one site's), re-read while shown. */
export function useRedundancyOverview(siteId: string | null) {
  return useQuery(redundancyOverviewQuery(useServices().backend.api, siteId));
}

/** What the production's standby believes is on air and next, re-read while shown. */
export function useStandbyPlayout(productionId: string) {
  return useQuery(standbyPlayoutQuery(useServices().backend.api, productionId));
}

export function useUpdateRedundancy() {
  return useMutation(updateRedundancyMutation(useServices().backend.api, useQueryClient()));
}

export function usePairSwitchover() {
  return useMutation(pairSwitchoverMutation(useServices().backend.api, useQueryClient()));
}

export function useSwitchoverAll() {
  return useMutation(switchoverAllMutation(useServices().backend.api, useQueryClient()));
}

export function useForcePromote() {
  return useMutation(forcePromoteMutation(useServices().backend.api, useQueryClient()));
}

export function useDeleteStudio() {
  return useMutation(deleteStudioMutation(useServices().backend.api, useQueryClient()));
}
